import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  BookOpen,
  Heart,
  Gavel,
  ChevronRight,
  Star,
  Youtube,
  MapPin,
  MessageCircle,
  Headphones,
} from 'lucide-react';
import heroBg from '../assets/images/IMG_0821.JPG';
import logo from '../assets/images/logo.jpg';

export default function Landing() {
  const [socialLinks] = useState({
    youtube: '#',
    whatsapp: '#',
    instagram: '#',
    telegram: '#',
    facebook: '#'
  });

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(${heroBg})`
          }}
        />
        <div className="hero-overlay absolute inset-0 bg-black/60" />

        {/* Navigation */}
        <nav className="absolute top-0 left-0 right-0 z-20 px-4 pb-4 md:px-6 md:pb-6 safe-top">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2 md:gap-4">
              <div className="w-12 h-12 md:w-20 md:h-20 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-white/20">
                <img src={logo} alt="Logo" className="w-full h-full object-cover" />
              </div>
              <span className="text-white text-lg md:text-xl font-semibold hidden md:block">Daara Wakana Haqqan</span>
            </div>
            <div className="flex items-center gap-2 md:gap-4">
              <Link to="/public-mediatheque" className="hidden md:block">
                <Button variant="ghost" className="text-white hover:bg-white/10 gap-2">
                  <Headphones className="h-4 w-4" />
                  Médiathèque
                </Button>
              </Link>
              <Link to="/login" className="hidden md:block">
                <Button variant="ghost" className="text-white hover:bg-white/10">
                  Connexion
                </Button>
              </Link>
              <Link to="/login">
                <Button className="bg-secondary text-secondary-foreground hover:bg-secondary/90 rounded-full px-4 md:px-6 text-sm md:text-base">
                  Espace Membre
                </Button>
              </Link>
            </div>
          </div>
        </nav>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto animate-fade-in">
          <p className="text-secondary font-medium tracking-[0.2em] uppercase text-sm mb-4">
            Dahira Mouride
          </p>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Daara Wakana Haqqan
          </h1>
          <p className="text-xl md:text-2xl text-white/80 mb-8 font-light max-w-2xl mx-auto">
            Service à la communauté est service au Divin
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register" data-testid="hero-join-btn">
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6 text-lg">
                Devenir Membre
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/login" data-testid="hero-login-btn">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg">
                Espace Membre
              </Button>
            </Link>
            <Link to="/public-mediatheque">
              <Button size="lg" variant="outline" className="border-secondary text-secondary hover:bg-secondary/10 hover:text-secondary rounded-full px-8 py-6 text-lg gap-2">
                <Headphones className="h-5 w-5" />
                Médiathèque
              </Button>
            </Link>
          </div>
          <a href="#reglement">
            <Button size="lg" variant="ghost" className="text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg border-2 border-transparent hover:border-white/20">
              Voir le Règlement
            </Button>
          </a>
        </div>


        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-white/50 rounded-full" />
          </div>
        </div>
      </section >

      {/* Regulation Section (Replaces Features) */}
      <section className="py-24 px-4 bg-muted/30" id="reglement">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-primary font-medium tracking-[0.2em] uppercase text-sm mb-3">
              Règlement
            </p>
            <h2 className="text-4xl md:text-5xl font-semibold text-foreground mb-4">
              Règlement Intérieur
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Les principes fondamentaux qui régissent la vie de notre communauté, garantissant l'harmonie et le respect des valeurs.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Discipline Card */}
            <Card className="card-hover border-border/50 bg-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Gavel className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground">
                    La Discipline
                  </h3>
                </div>
                <div className="space-y-4">
                  {[
                    { id: 1, text: "Tout adhérant au Dahira doit connaitre et disposer du règlement intérieur sur la base duquel il devra s'engager." },
                    { id: 2, text: "Tout membre du Dahira au moment de s'inscrire doit donner une caution symbolique de 1140 f qui montre qu'il est officiellement membre." },
                    { id: 3, text: "Si le nouveau membre n'est pas majeur, le/la concerné (e) doit obligatoirement avoir une autorisation parentale." },
                    { id: 4, text: "Après 2 absences successives lors des Dahira sans en faire part au Dieuwrign, une commission spécialisée ira s'enquérir de la situation et après 3 absences sans justification le concerné sera traduit en conseil de discipline." },
                    { id: 5, text: "La présence du Dahira de dimanche est obligatoire et en cas d'absence, le/la concerné(e) devrait en tenir informé le Dieuwrign." },
                    { id: 6, text: "Chaque membre doit respecter les bonnes mœurs au sein du Dahira comme en dehors du Dahira. En cas de non respect le ou la concerné(e) sera traduit(e ) en C.D" }
                  ].map((article) => (
                    <div key={article.id} className="p-4 rounded-lg bg-muted/50">
                      <span className="font-semibold text-primary block mb-1">Article {article.id}</span>
                      <p className="text-muted-foreground text-sm leading-relaxed">{article.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Culturel Card */}
            <Card className="card-hover border-border/50 bg-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <Heart className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground">
                    Le Culturel
                  </h3>
                </div>
                <div className="space-y-4">
                  {[
                    { id: 1, text: "Tous les membres des kourels de khassida doivent respecter l’heure fixée pour la répétition et le dahira de dimanche (16h45)." },
                    { id: 2, text: "Tout membre du Dahira présent sur le lieu du dahira de dimanche doit s’accoutrer de manière descente « Goor Yalla » comme « Sokhna ». Ce dernier est aussi appelé de se munir d’un diouki et se diriger vers le Qibla." },
                    { id: 3, text: "En cas de non-respect de ces chartes ci-dessus la commission concernée se chargera de rappeler l’ordre à ce dernier." },
                    { id: 4, text: "Chaque membre doit respecter le programme Majalis établi par la commission en question." }
                  ].map((article) => (
                    <div key={article.id} className="p-4 rounded-lg bg-muted/50">
                      <span className="font-semibold text-primary block mb-1">Article {article.id}</span>
                      <p className="text-muted-foreground text-sm leading-relaxed">{article.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Finance Card */}
            <Card className="card-hover border-border/50 bg-card">
              <CardContent className="p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <BookOpen className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-semibold text-foreground">
                    La Finance
                  </h3>
                </div>
                <div className="space-y-4">
                  {[
                    { id: 1, text: "Chaque membre doit donner son engagement personnel et individuel sur la manière et le montant de sa cotisation mensuelle (1000 f) et celle des dahiras de dimanche (150 f)." },
                    { id: 2, text: "Au bout de 3 mois de non-respect des cotisations, le concerné sera convoqué par la commission spécialisée pour des explications et en cas d’explication non valable il sera traduit en conseil de discipline." },
                    { id: 3, text: "Chaque membre doit scrupuleusement respecter le versement de sa cotisation dénommée « SASS » durant les événements." },
                    { id: 4, text: "Tout membre a l’obligation de donner sa participation financière ou matérielle sur la caisse sociale." }
                  ].map((article) => (
                    <div key={article.id} className="p-4 rounded-lg bg-muted/50">
                      <span className="font-semibold text-primary block mb-1">Article {article.id}</span>
                      <p className="text-muted-foreground text-sm leading-relaxed">{article.text}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mt-12 text-center">
            <a href="/Réglement intérieur.pptx" download>
              <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary/10 rounded-full px-8">
                Télécharger la Présentation (PPT)
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
          </div>
        </div>
      </section >

      {/* Quote Section */}
      <section className="py-24 px-4 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <Star className="h-12 w-12 text-secondary mx-auto mb-6" />
          <blockquote className="text-2xl md:text-3xl font-light italic mb-6 font-arabic">
            "Travaille comme si tu ne devais jamais mourir et prie comme si tu devais mourir demain"
          </blockquote>
          <p className="text-secondary font-medium">
            — Cheikh Ahmadou Bamba
          </p>
        </div>
      </section >

      {/* Location Section */}
      <section className="py-24 px-4 bg-background">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row gap-12 items-center">
          <div className="flex-1 space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <MapPin className="w-4 h-4" />
              Localisation
            </div>
            <h2 className="text-4xl font-semibold text-foreground">
              Notre Siège
            </h2>
            <p className="text-muted-foreground text-lg">
              Venez nous rendre visite. Nous sommes situés dans un cadre propice à l'apprentissage et au recueillement à Parcelles Assainies, Dakar.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a href="https://maps.apple.com/place?coordinate=14.760987,-17.434336&name=Siège%20Daara%20Wakana%20Haqqan&map=h" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="gap-2 w-full sm:w-auto">
                  Ouvrir dans Apple Maps <ChevronRight className="w-4 h-4" />
                </Button>
              </a>
              <a href="https://www.google.com/maps/search/?api=1&query=14.760987,-17.434336" target="_blank" rel="noopener noreferrer">
                <Button className="gap-2 w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
                  Ouvrir dans Google Maps <ChevronRight className="w-4 h-4" />
                </Button>
              </a>
            </div>
          </div>
          <div className="flex-1 w-full h-[400px] rounded-2xl overflow-hidden shadow-xl border border-border/50 bg-card p-1">
            <iframe
              width="100%"
              height="100%"
              frameBorder="0"
              scrolling="no"
              marginHeight="0"
              marginWidth="0"
              title="Localisation Siège"
              src="https://maps.google.com/maps?q=14.760987,-17.434336&hl=fr&z=15&output=embed"
              className="rounded-xl w-full h-full"
            />
          </div>
        </div>
      </section >

      {/* CTA Section */}
      <section className="py-24 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-semibold text-foreground mb-6">
            Rejoignez Notre Communauté
          </h2>
          <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
            Devenez membre du Dahira Daara Wakana Haqqan et participez à la vie spirituelle et communautaire.
          </p>
          <Link to="/register" data-testid="cta-join-btn">
            <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-10 py-6 text-lg">
              S'inscrire Maintenant
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section >

      {/* Footer */}
      <footer className="bg-foreground text-background py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center overflow-hidden border-2 border-white/20">
                <img src={logo} alt="Logo" className="w-full h-full object-cover" />
              </div>
              <span className="text-xl font-semibold">Daara Wakana Haqqan</span>
            </div>

            <div className="flex items-center gap-4">
              <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors" data-testid="social-youtube">
                <Youtube className="h-6 w-6" />
              </a>
              <a href={socialLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="hover:text-secondary transition-colors" data-testid="social-whatsapp">
                <MessageCircle className="h-6 w-6" />
              </a>
            </div>
          </div>

          <div className="border-t border-background/20 mt-8 pt-8 text-center text-background/60">
            <p>© 2025 Dahira Daara Wakana Haqqan. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
